import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Accordion,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Row,
  Spinner
} from "react-bootstrap";
import { useAuth } from "../components/auth/AuthProvider";
import {
  getDocumentGroups
} from "../controllers/documents";

function fileIcon(mimeType) {
  if (!mimeType) return '📄';
  if (mimeType === 'application/pdf') return '📕';
  if (mimeType.startsWith('image/')) return '🖼️';
  if (mimeType.includes('word')) return '📝';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
  if (mimeType.includes('powerpoint') || mimeType.includes('presentationml')) return '📽️';
  return '📄';
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function buildAuthHeaders(token) {
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

function getDownloadFileName(doc, response) {
  const disposition = response.headers.get('content-disposition') || '';
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    }
    catch (_e) {
      return utf8Match[1];
    }
  }

  const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
  if (filenameMatch?.[1]) {
    return filenameMatch[1];
  }

  return doc.fileName || doc.title || 'documento';
}

function Documentos() {
  const { token } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const displayGroups = useMemo(() => {
    return groups
      .filter(group => group.active)
      .map(group => ({
        ...group,
        documents: (group.documents || []).filter(doc => doc.active)
      }));
  }, [groups]);

  useEffect(() => {
    document.title = 'FAMA - Documentos';
  }, []);

  useEffect(() => {
    loadData();
  }, [token]);

  async function loadData() {
    setLoading(true);
    setError(null);
    try {
      const data = await getDocumentGroups();
      setGroups(data);
    } catch (e) {
      setError('No se pudieron cargar los documentos. Inténtelo más tarde.');
    } finally {
      setLoading(false);
    }
  }

  async function handleDownload(e, doc) {
    e.preventDefault();
    if (!doc?.url) {
      setError('No se encontró la URL del documento.');
      return;
    }

    setDownloadingId(doc.id);
    setError(null);

    try {
      const response = await fetch(doc.url, {
        method: 'GET',
        headers: buildAuthHeaders(token)
      });

      if (!response.ok) {
        throw new Error('No se pudo descargar el documento.');
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = getDownloadFileName(doc, response);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (_e) {
      setError('No se pudo descargar el documento. Inténtelo de nuevo.');
    } finally {
      setDownloadingId(null);
    }
  }

  const publicSection = (
    <>
      {!loading && !error && displayGroups.length === 0 && (
        <p className="text-muted">No hay documentos disponibles en este momento.</p>
      )}

      <Accordion defaultActiveKey={displayGroups.length ? '0' : undefined}>
        {displayGroups.map((group, idx) => (
          <Accordion.Item eventKey={String(idx)} key={group.id}>
            <Accordion.Header>
              <strong>{group.label}</strong>
              <Badge bg="secondary" className="ms-2">{group.documents.length}</Badge>
            </Accordion.Header>
            <Accordion.Body>
              {group.documents.length === 0 && (
                <p className="text-muted mb-0">No hay documentos en este grupo.</p>
              )}
              <Row xs={1} md={2} lg={3} className="g-3">
                {group.documents.map(doc => (
                  <Col key={doc.id}>
                    <Card className="h-100 shadow-sm">
                      <Card.Body>
                        <Card.Title className="fs-6">
                          <span className="me-2">{fileIcon(doc.mimeType)}</span>
                          {doc.title}
                        </Card.Title>
                        {doc.description && (
                          <Card.Text className="text-muted small">{doc.description}</Card.Text>
                        )}
                        <div className="text-muted small mb-2">{formatSize(doc.size)}</div>
                        <Button
                          size="sm"
                          variant="primary"
                          disabled={downloadingId === doc.id}
                          onClick={(e) => handleDownload(e, doc)}
                        >
                          {downloadingId === doc.id ? 'Descargando...' : 'Ver / Descargar'}
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>
    </>
  );

  return (
    <Container className="pt-5 pb-5">
      <Row className="mb-4">
        <Col>
          <h1>Documentos</h1>
          <p className="text-muted">Documentos y recursos disponibles para los miembros de FAMA.</p>
        </Col>
      </Row>

      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      )}

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {!loading && publicSection}
    </Container>
  );
}

export default Documentos;
